import type { TypedDocumentNode } from '@apollo/client';
import { gql, useApolloClient } from '@apollo/client';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  Cryptocurrency,
  type Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS, HeadlineXS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import {
  AcceptedCurrenciesValue,
  useAcceptedCurrencies,
} from '@sorare/core/src/hooks/useAcceptedCurrencies';
import { useCryptoBalance } from '@sorare/core/src/hooks/useCryptoBalance';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { tokenHitFragment, type CardHit } from '@sorare/core/src/lib/algolia';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { tradeLabels } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { WalletPaymentMethod } from '@sorare/core/src/lib/paymentMethod';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardPickerDialog as CardPicker } from '#src/components/directOffer/NewOfferBuilder/CardPicker/index.tsx';
import { messages as offerSideMessages } from '#src/components/directOffer/NewOfferBuilder/OfferSide/i18n.ts';
import { OfferSide } from '#src/components/directOffer/NewOfferBuilder/OfferSide/index.tsx';
import {
  refreshCardData,
  switchToConfirming,
  updateReceiveCards,
  updateSendCards,
} from '#src/components/directOffer/NewOfferBuilder/actions.ts';
import type { StateProps } from '#src/components/directOffer/NewOfferBuilder/types.ts';
import { useStateOffer } from '#src/hooks/offers/useStateOffer.ts';
import { useHasInsufficientFundsInWallets } from '#src/hooks/useHasInsufficientFundsInWallets.ts';
import { useMarketFeesHelperStatus } from '#src/hooks/useMarketFeesHelperStatus.ts';

import { AmountInput } from './AmountInput/index.tsx';
import { InputDuration } from './InputDuration/index.tsx';
import { TradePaymentMethods } from './TradePaymentMethods/index.tsx';
import type {
  OfferBuilderBuildingPage_anyCard,
  OfferBuilderBuildingPage_baseCard,
  OfferBuilderBuildingPage_publicUserInfoInterface,
} from './__generated__/index.graphql.ts';

const StyledTooltip = styled(Tooltip)`
  width: 100%;
`;

const Container = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
  padding: 0 0 calc(var(--marginBottom) * 1px);
  margin: 0;
  min-width: 100%;
`;
const WarningContainer = styled.div`
  padding: var(--double-unit);
`;
const ETHOnlyWarningContainer = styled.div`
  display: inline-flex;
  gap: var(--double-unit);
  align-items: center;
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  & > :not(:first-child) {
    border-top: 1px solid var(--c-nd-100);
  }
  & > :last-child {
    margin-bottom: var(--double-unit);
  }
  & > * {
    padding: var(--double-unit) var(--triple-unit);
    flex-grow: 1;
    width: 100%;
  }
  @media ${tabletAndAbove} {
    & > :not(:first-child) {
      border-top: none;
    }
    flex-direction: row;
    align-items: flex-start;
  }
`;
const ActionWrapper = styled.div`
  padding: var(--triple-unit);
  margin-top: auto;
  box-shadow: 0px 14px 50px rgba(0, 0, 0, 0.2);
`;

const WarningHelper = styled(Horizontal)`
  padding: var(--intermediate-unit);
  background-color: var(--c-nd-100);
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
  color: var(--c-white);
`;

const CounterOfferWarning = styled.div`
  padding: var(--unit) var(--double-unit);
  background-color: var(--c-surface-caution);
  border-radius: var(--unit);
  border: var(--c-yellow-500) solid 1px;
`;

interface Props<DATA, QUERY_RESULT extends { anyCards: DATA[] }>
  extends StateProps<DATA> {
  to: OfferBuilderBuildingPage_publicUserInfoInterface;
  onClose: () => void;
  query: TypedDocumentNode<QUERY_RESULT, { slugs: string[] }>;
  counterOfferId?: string;
  counterOfferSport?: Sport;
  sender: ReactNode;
  receiver: ReactNode;
  lockReceiveEthInput?: boolean;
}

const anyCardFragment = gql`
  fragment OfferBuilderBuildingPage_baseCard on AnyCardInterface {
    slug
    publicMinPrices {
      ...MonetaryAmountFragment_monetaryAmount
    }
    ...OfferSide_anyCard
  }
  ${monetaryAmountFragment}
  ${OfferSide.fragments.anyCard}
` as TypedDocumentNode<OfferBuilderBuildingPage_baseCard>;

const messages = defineMessages({
  sendTrade: {
    id: 'OfferBuilder.BuildingPage.sendTrade',
    defaultMessage: 'Send the trade',
  },
  sendCounterOffer: {
    id: 'OfferBuilder.BuildingPage.sendCounterOffer',
    defaultMessage: 'Review counter offer',
  },
  counterOfferWarning: {
    id: 'OfferBuilder.BuildingPage.counterOfferWarning',
    defaultMessage:
      '💡 Sending this counter offer will reject the previous offer.',
  },
});

export const BuildingPage = <
  DATA extends OfferBuilderBuildingPage_anyCard,
  QUERY_RESULT extends { anyCards: DATA[] },
>({
  to,
  state,
  dispatch,
  onClose,
  query,
  counterOfferId,
  counterOfferSport,
  sender,
  receiver,
  lockReceiveEthInput,
}: Props<DATA, QUERY_RESULT>) => {
  const {
    flags: { useCannotSwapCrytpo = true },
  } = useFeatureFlags();

  const { cryptocurrency: currentUserCryptocurrency } =
    useCurrentUserCurrencies();
  const toCryptocurrency = to.cryptocurrency || Cryptocurrency.WEI;

  const { acceptedCurrencies } = useAcceptedCurrencies({ user: to });
  const setConfirming = useCallback(
    () => dispatch(switchToConfirming),
    [dispatch]
  );
  const {
    sendAmount,
    receiveAmount,
    sendCards,
    cardsData,
    receiveCards,
    stage,
    paymentMethod,
  } = state;

  const { isValid: valid, isTradeForNothing } = useStateOffer(state);

  const sendAmountGtZero = sendAmount.isPositive;
  const receiveAmountGtZero = receiveAmount.isPositive;

  const canSwapCrypto =
    (useCannotSwapCrytpo && currentUserCryptocurrency === toCryptocurrency) ||
    !useCannotSwapCrytpo;

  const acceptCryptoWallet =
    [AcceptedCurrenciesValue.BOTH, AcceptedCurrenciesValue.CRYPTO].includes(
      acceptedCurrencies
    ) && canSwapCrypto;

  const acceptCashWallet = [
    AcceptedCurrenciesValue.BOTH,
    AcceptedCurrenciesValue.FIAT,
  ].includes(acceptedCurrencies);

  const hasInsufficientFundsInWallets = useHasInsufficientFundsInWallets();
  const { insufficientFundsInCryptoWallet, insufficientFundsInFiatWallet } =
    hasInsufficientFundsInWallets(sendAmount);

  const invalid =
    !valid ||
    (insufficientFundsInCryptoWallet &&
      paymentMethod === WalletPaymentMethod.CRYPTO_WALLET) ||
    (insufficientFundsInFiatWallet &&
      paymentMethod === WalletPaymentMethod.FIAT_WALLET);
  const client = useApolloClient();
  const { showNotification } = useSnackNotificationContext();
  const [loading, setLoading] = useState(false);

  const { up: isTablet } = useScreenSize('tablet');
  const marketFeeStatus = useMarketFeesHelperStatus(
    sendCards.map(c => cardsData[c.slug]).filter(Boolean)
  );
  const { sport } = useSportContext();

  const fetchMissingCardsData = useCallback(
    async (newState: CardHit[]) => {
      try {
        const newCardData = { ...state.cardsData };
        const missingGqlDataHits = newState.filter(c => !newCardData[c.slug]);
        const slugs = missingGqlDataHits.map(cardHit => cardHit.slug);
        const { data } = await client.query<QUERY_RESULT, { slugs: string[] }>({
          query,
          variables: {
            slugs: slugs.filter(Boolean),
          },
        });

        newState.forEach(c => {
          const associatedToken = data.anyCards.find(card => {
            return card.slug === c.slug;
          });
          if (associatedToken) newCardData[c.slug] = associatedToken;
        });
        dispatch(refreshCardData(newCardData));
        return Promise.resolve(true);
      } catch (e) {
        sendSafeError(e);
        if (e && typeof e === 'object' && 'message' in e) {
          showNotification('errors', { errors: e.message });
        }
        return Promise.resolve(false);
      } finally {
        setLoading(false);
      }
    },
    [client, dispatch, query, showNotification, state.cardsData]
  );

  const setSendCards = useCallback(
    async (cards: CardHit[]) => {
      const result = await fetchMissingCardsData(cards);
      if (result) {
        dispatch(updateSendCards(cards));
      }
      return result;
    },
    [dispatch, fetchMissingCardsData]
  );

  const setReceiveCards = useCallback(
    async (cards: CardHit[]) => {
      const result = await fetchMissingCardsData(cards);
      if (result) {
        dispatch(updateReceiveCards(cards));
      }
      return result;
    },
    [dispatch, fetchMissingCardsData]
  );

  const {
    currentUser,
    walletPreferences: { enabledWallets, onlyShowFiatCurrency },
  } = useCurrentUserContext();

  const { cryptocurrencySymbol } = useCryptoBalance();

  const [cardSelectionState, setCardSelectionState] = useState<
    | {
        owner: { id: string; slug: string };
        setCards: (cards: CardHit[]) => void;
        selectedCards: CardHit[];
      }
    | undefined
  >(undefined);

  const closeCardPicker = useCallback(
    () => setCardSelectionState(undefined),
    [setCardSelectionState]
  );

  const closeCardPickerOnSuccess = useCallback(
    (success: boolean) => {
      if (success) {
        closeCardPicker();
      }
    },
    [closeCardPicker]
  );

  const openReceiveCardSelectionPopup = useCallback(() => {
    setCardSelectionState({
      owner: to,
      selectedCards: receiveCards,
      setCards: (cards: CardHit[]) => {
        setReceiveCards(cards).then(closeCardPickerOnSuccess);
      },
    });
  }, [receiveCards, to, setReceiveCards, closeCardPickerOnSuccess]);

  const openSendCardSelectionPopup = useCallback(() => {
    setCardSelectionState({
      owner: currentUser!,
      selectedCards: sendCards,
      setCards: (cards: CardHit[]) => {
        setSendCards(cards).then(closeCardPickerOnSuccess);
      },
    });
  }, [sendCards, currentUser, setSendCards, closeCardPickerOnSuccess]);

  const canNotTradeMoney =
    (!acceptCryptoWallet && !acceptCashWallet) ||
    (!acceptCashWallet && onlyShowFiatCurrency) ||
    (!acceptCryptoWallet && !enabledWallets);

  if (cardSelectionState) {
    return (
      <CardPicker
        onClose={closeCardPicker}
        title={offerSideMessages.addCard}
        selectedCards={cardSelectionState.selectedCards}
        confirmSelectedCards={cardSelectionState.setCards}
        fetchMissingCardsData={fetchMissingCardsData}
        cardsData={state.cardsData}
        owner={cardSelectionState.owner}
        counterOfferSport={counterOfferSport}
        maxCards={100}
      />
    );
  }

  const renderCta = () => {
    const cta = (
      <LoadingButton
        color="primary"
        size="medium"
        onClick={setConfirming}
        loading={stage === 'submitting' || loading}
        disabled={invalid}
        fullWidth
      >
        <FormattedMessage
          {...(counterOfferId ? messages.sendCounterOffer : messages.sendTrade)}
        />
      </LoadingButton>
    );
    if (isTradeForNothing)
      return (
        <StyledTooltip
          leaveTouchDelay={3000}
          title={
            <BodyS>
              {!!sendAmountGtZero && receiveCards.length === 0 && (
                <FormattedMessage
                  id="OfferBuilder.BuildingPage.tradeForNothingWarning"
                  defaultMessage="You cannot send this trade without receiving at least one Card"
                />
              )}
              {!!receiveAmountGtZero && sendCards.length === 0 && (
                <FormattedMessage
                  id="OfferBuilder.BuildingPage.tradeForNothingWarningSending"
                  defaultMessage="You cannot send this trade without sending at least one Card"
                />
              )}
            </BodyS>
          }
          placement="top"
        >
          {cta}
        </StyledTooltip>
      );
    return cta;
  };

  const cashOnlyOffersSportPreference = to.profile.marketplacePreferences
    .find(
      marketplacePreference =>
        marketplacePreference.sport === (sport || counterOfferSport)
    )
    ?.preferences.find(({ name }) => name === 'cash_only_offers');

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      onClose={onClose}
      fullScreen={!isTablet}
      title={
        <HeadlineXS as="h6" className="text-center">
          <FormattedMessage
            {...(counterOfferId
              ? tradeLabels.counterOfferWith
              : tradeLabels.tradeWith)}
            values={{
              nickname: to.nickname,
            }}
          />
        </HeadlineXS>
      }
    >
      <Container>
        {!!counterOfferId && (
          <WarningContainer>
            <CounterOfferWarning>
              <BodyS>
                <FormattedMessage {...messages.counterOfferWarning} />
              </BodyS>
            </CounterOfferWarning>
          </WarningContainer>
        )}
        <Row>
          <OfferSide
            cards={sendCards}
            cardsData={cardsData}
            setCards={setSendCards}
            title={sender}
            toggleAddCardOpened={openSendCardSelectionPopup}
            isOwnSide
            addCardDisabledWarning={
              !!cashOnlyOffersSportPreference?.value && (
                <Warning variant="yellow">
                  <ETHOnlyWarningContainer>
                    <FontAwesomeIcon icon={faCircleInfo} size="1x" />
                    <BodyS>
                      <FormattedMessage
                        id="NewOfferBuilder.OfferSide.managerOnlyAcceptingMoney"
                        defaultMessage="{manager} is only accepting money on this trade."
                        values={{
                          manager: to.nickname,
                        }}
                      />
                    </BodyS>
                  </ETHOnlyWarningContainer>
                </Warning>
              )
            }
          >
            {canNotTradeMoney ? (
              <>
                {!onlyShowFiatCurrency && (
                  <WarningHelper>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <BodyS>
                      <FormattedMessage
                        id="NewOfferBuilder.TradePaymentMethods.cantAcceptCrypto"
                        defaultMessage="<b>{nickname}</b> can't accept {cryptocurrencySymbol}."
                        values={{
                          b: Bold,
                          nickname: to.nickname,
                          cryptocurrencySymbol,
                        }}
                      />
                    </BodyS>
                  </WarningHelper>
                )}
                {!acceptCashWallet && (
                  <WarningHelper>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <BodyS>
                      <FormattedMessage
                        id="NewOfferBuilder.TradePaymentMethods.cantAcceptCash"
                        defaultMessage="<b>{nickname}</b> can't accept Cash."
                        values={{ b: Bold, nickname: to.nickname }}
                      />
                    </BodyS>
                  </WarningHelper>
                )}
              </>
            ) : (
              <AmountInput state={state} dispatch={dispatch} />
            )}

            {!canNotTradeMoney && !!enabledWallets && (
              <TradePaymentMethods
                state={state}
                dispatch={dispatch}
                onClose={onClose}
                to={to}
              />
            )}
          </OfferSide>
          <OfferSide
            cards={receiveCards}
            cardsData={cardsData}
            setCards={setReceiveCards}
            title={receiver}
            toggleAddCardOpened={openReceiveCardSelectionPopup}
            displayMinPrices
          >
            {!!counterOfferId && !lockReceiveEthInput && (
              <AmountInput
                state={state}
                dispatch={dispatch}
                receiver
                marketFeeStatus={marketFeeStatus}
              />
            )}
          </OfferSide>
        </Row>
        <Row>
          <InputDuration state={state} dispatch={dispatch} to={to} />
        </Row>
        <ActionWrapper>{renderCta()}</ActionWrapper>
      </Container>
    </Dialog>
  );
};

BuildingPage.fragments = {
  user: gql`
    fragment OfferBuilderBuildingPage_publicUserInfoInterface on PublicUserInfoInterface {
      id
      slug
      nickname
      cryptocurrency
      profile {
        id
        marketplacePreferences(sports: [FOOTBALL, NBA, BASEBALL]) {
          sport
          preferences {
            name
            value
          }
        }
      }
      hoursToAnswerTrades
      ...TradePaymentMethods_publicUserInfoInterface
    }
    ${TradePaymentMethods.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<OfferBuilderBuildingPage_publicUserInfoInterface>,
  anyCard: gql`
    fragment OfferBuilderBuildingPage_anyCard on AnyCardInterface {
      slug
      sport
      ...Algolia_CardHit_anyCard
      ...useMarketFeesHelperStatus_anyCard
      ...OfferBuilderBuildingPage_baseCard
      ...CardPicker_anyCard
      ...useStateOffer_anyCard
    }
    ${anyCardFragment}
    ${useStateOffer.fragments.anyCard}
    ${tokenHitFragment}
    ${useMarketFeesHelperStatus.fragments.anyCard}
    ${CardPicker.fragments.anyCard}
  ` as TypedDocumentNode<OfferBuilderBuildingPage_anyCard>,
};
